// Settings — `/dashboard/settings` self-serve account settings page.
//
// Currently scoped to "綁定 LINE"; future work (notification preferences,
// password change, session list) lands on this page too.
//
// For LINE binding the dashboard is HTML/desktop-first while the
// `/liff/me` page is the only place we can verifiably get a LIFF
// id_token from `liff.getIDToken()`. So this card's job is to bridge:
// either let the user open the LIFF page on their phone (mobile click)
// or scan a QR code from desktop.

import React, { useCallback, useEffect, useState } from "react";
import { Link2, Smartphone, RefreshCw, CheckCircle2, AlertCircle, ExternalLink, Brain, Trash2, Building2, Save } from "lucide-react";
import QRCode from "qrcode";
import PageHeader from "../components/PageHeader.js";
import Skeleton from "../components/Skeleton.js";
import MyPermissionsCard from "../components/MyPermissionsCard.js";
import MCPTokensCard from "../components/MCPTokensCard.js";
import {
  getLineStatus,
  unlinkLine,
  friendlySettingsError,
  type LineStatus,
} from "../lib/settingsApi.js";
import {
  listAiFacts,
  deleteAiFact,
  wipeAiFacts,
  friendlyChatError,
  type AiFactRow,
  type AiFactKind,
} from "../lib/chatApi.js";
import {
  listTenants,
  patchTenant,
  friendlyTenantsError,
  type TenantRow,
} from "../lib/tenantsApi.js";
import { useConfirmDialog } from "../components/ConfirmDialog.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { Field } from "../components/Field.js";
import { formatTaipeiDateTime } from "../../lib/time.js";

const LIFF_ID_ME = (import.meta.env.VITE_LIFF_ID_ME as string | undefined) ?? "";
const LIFF_ME_URL = LIFF_ID_ME
  ? `https://liff.line.me/${LIFF_ID_ME}`
  // Fallback to the direct path if the LIFF id env wasn't injected at
  // build time. Opens the page in the user's browser; LIFF SDK on the
  // page will redirect into LINE if needed.
  : `${window.location.origin}/liff/me`;

type Stage =
  | { status: "loading" }
  | { status: "ready"; line: LineStatus }
  | { status: "error"; message: string };

export default function Settings(): React.ReactElement {
  const session = useDashboardSession();
  // 「單位品牌」 section — 取代舊的 /dashboard/tenants 單列頁。給
  // tenant_admin 改自家名稱 + 品牌色；super_admin 看不到（他們有
  // /dashboard/platform/tenants 完整工具）。
  const canEditOrg = session.role === "tenant_admin";
  const [stage, setStage] = useState<Stage>({ status: "loading" });
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setStage({ status: "loading" });
    try {
      const line = await getLineStatus();
      setStage({ status: "ready", line });
    } catch (err) {
      setStage({ status: "error", message: friendlySettingsError(err) });
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const onUnlink = async () => {
    if (!window.confirm("確定要解除 LINE 綁定？解除後您將不再收到 LINE 上的通知與選單操作；可隨時重新綁定。")) return;
    setBusy(true);
    try {
      await unlinkLine();
      setToast("已解除 LINE 綁定。");
      void load();
    } catch (err) {
      setToast(friendlySettingsError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="個人設定"
        description="管理您與 LINE 的綁定、通知偏好等。"
        actions={
          <button className="btn-secondary text-sm" onClick={() => void load()} disabled={stage.status === "loading"}>
            <RefreshCw className={`h-4 w-4 ${stage.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
          </button>
        }
      />

      {toast && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-success/40 bg-success/10 p-3 text-sm text-success">
          <CheckCircle2 className="h-4 w-4" />
          <span className="flex-1">{toast}</span>
          <button className="text-2xs underline" onClick={() => setToast(null)}>關閉</button>
        </div>
      )}

      <div className="card p-4 sm:p-5">
        <div className="flex items-start gap-3 mb-4">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-bg-raised border border-border">
            <Link2 className="h-4 w-4 text-accent" />
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-text-primary">綁定 LINE 帳號</h2>
            <p className="mt-0.5 text-xs text-text-secondary leading-snug">
              綁定後您可以直接在 LINE 收到逾時、異常與排班通知；也能在 LINE 中
              開啟綁定頁面查看案場巡邏即時狀態。一個 LINE 帳號只能綁一個系統使用者。
            </p>
          </div>
        </div>

        {stage.status === "loading" && <Skeleton className="h-24 w-full" />}
        {stage.status === "error" && (
          <div className="flex items-start gap-2 rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{stage.message}</span>
          </div>
        )}
        {stage.status === "ready" && stage.line.bound && (
          <BoundCard line={stage.line} busy={busy} onUnlink={() => void onUnlink()} />
        )}
        {stage.status === "ready" && !stage.line.bound && <UnboundCard />}
      </div>

      {canEditOrg && <OrgBrandingCard onSaved={() => setToast("單位品牌已更新")} />}

      <div className="mt-4">
        <MyPermissionsCard />
      </div>

      <MCPTokensCard />

      <AiMemoryCard />
    </>
  );
}

// ─── 單位品牌（tenant_admin 自家組織資料）──────────────────────────────
// 取代被移除的 /dashboard/tenants 單列頁。讀 listTenants（後端對
// non-super_admin 自動 filter 到自家那一筆）→ 顯示 read-only 的 plan / 狀態
// + 可編輯的名稱 + 主視覺色 + 輔色 → patchTenant 寫回。

type OrgStage =
  | { status: "loading" }
  | { status: "ready"; tenant: TenantRow }
  | { status: "error"; message: string };

function OrgBrandingCard({ onSaved }: { onSaved: () => void }): React.ReactElement {
  const [stage, setStage] = useState<OrgStage>({ status: "loading" });
  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [brand2, setBrand2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setStage({ status: "loading" });
    setErr(null);
    try {
      const r = await listTenants();
      const tenant = r.items[0];
      if (!tenant) {
        setStage({ status: "error", message: "找不到您所屬的單位資料。" });
        return;
      }
      setStage({ status: "ready", tenant });
      setName(tenant.name);
      setBrand(tenant.brand_primary ?? "#00d4ff");
      setBrand2(tenant.brand_secondary ?? "");
    } catch (e) {
      setStage({ status: "error", message: friendlyTenantsError(e) });
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    if (stage.status !== "ready") return;
    setBusy(true); setErr(null);
    try {
      await patchTenant(stage.tenant.id, {
        name: name.trim(),
        brand_primary: brand,
        brand_secondary: brand2 || "",  // backend coerces empty string → NULL
      });
      onSaved();
      await load();
    } catch (e) {
      setErr(friendlyTenantsError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4 sm:p-5 mt-4">
      <div className="flex items-start gap-3 mb-4">
        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-bg-raised border border-border">
          <Building2 className="h-4 w-4 text-accent" />
        </span>
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-text-primary">單位品牌</h2>
          <p className="mt-0.5 text-xs text-text-secondary leading-snug">
            單位顯示名稱與主視覺色，會出現在 dashboard header、邀請碼頁面與通知卡片。方案與啟用狀態由 SENTRY 平台管理員調整。
          </p>
        </div>
      </div>

      {stage.status === "loading" && <Skeleton className="h-32 w-full" />}
      {stage.status === "error" && (
        <div className="flex items-start gap-2 rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{stage.message}</span>
        </div>
      )}
      {stage.status === "ready" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="單位代號 (slug)" hint="一經建立無法變更">
              <input className="input font-mono" value={stage.tenant.slug} disabled />
            </Field>
            <Field label="方案 / 狀態" hint="需 SENTRY 平台管理員調整">
              <input
                className="input"
                disabled
                value={`${stage.tenant.plan_label ?? stage.tenant.plan_id} ・ ${stage.tenant.status}`}
              />
            </Field>
          </div>
          <Field label="顯示名稱" required>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="主視覺色" hint="HEX 例如 #00d4ff">
              <div className="flex gap-2">
                <input type="color" className="h-9 w-12 rounded border border-border" value={brand || "#00d4ff"} onChange={(e) => setBrand(e.target.value)} />
                <input className="input font-mono flex-1" value={brand} onChange={(e) => setBrand(e.target.value)} />
              </div>
            </Field>
            <Field label="輔色" hint="可不填">
              <div className="flex gap-2">
                <input type="color" className="h-9 w-12 rounded border border-border" value={brand2 || "#000000"} onChange={(e) => setBrand2(e.target.value)} />
                <input className="input font-mono flex-1" value={brand2} onChange={(e) => setBrand2(e.target.value)} />
              </div>
            </Field>
          </div>
          {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}
          <div className="flex justify-end">
            <button className="btn-primary text-sm" onClick={() => void save()} disabled={busy || !name.trim()}>
              <Save className="h-4 w-4" /> {busy ? "儲存中…" : "儲存"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AI long-term memory card (PR #7) ───────────────────────────────────
// Lists facts the AI extracted from past chats, lets the user delete one
// or all. Per spec §5.3 these facts are stored in English; we tag them
// with the kind enum and confidence so the user can decide what to keep.

const KIND_LABEL_ZH: Record<AiFactKind, string> = {
  preference: "個人偏好",
  role_context: "角色脈絡",
  confusion: "曾困惑的點",
  workflow_pref: "工作流程偏好",
  tenant_setup_state: "目前狀態",
};

type FactsStage =
  | { status: "loading" }
  | { status: "ready"; facts: AiFactRow[] }
  | { status: "error"; message: string };

function AiMemoryCard(): React.ReactElement {
  const [stage, setStage] = useState<FactsStage>({ status: "loading" });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [wiping, setWiping] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const { confirm } = useConfirmDialog();

  const load = useCallback(async () => {
    setStage({ status: "loading" });
    try {
      const facts = await listAiFacts();
      setStage({ status: "ready", facts });
    } catch (err) {
      setStage({ status: "error", message: friendlyChatError(err) });
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const onDelete = async (f: AiFactRow): Promise<void> => {
    const ok = await confirm(
      `「${f.content_zh ?? f.content}」（${KIND_LABEL_ZH[f.kind]}）。刪除後 AI 下次回答不會再用到這條，無法復原。`,
      { title: "刪除這條記憶？", okLabel: "刪除", destructive: true },
    );
    if (!ok) return;
    setBusyId(f.id);
    try {
      await deleteAiFact(f.id);
      setToast("已刪除一條記憶。");
      void load();
    } catch (err) {
      setToast(friendlyChatError(err));
    } finally {
      setBusyId(null);
    }
  };

  const onWipe = async (): Promise<void> => {
    const ok = await confirm(
      "AI 對您的所有長期記憶都會被刪除（個人偏好、角色脈絡、工作流程習慣…）。下次對話 AI 將從零開始重新認識您。此動作無法復原。",
      { title: "清空全部 AI 記憶？", okLabel: "清空全部", destructive: true },
    );
    if (!ok) return;
    setWiping(true);
    try {
      const r = await wipeAiFacts();
      setToast(`已清空 ${r.deleted} 條記憶。`);
      void load();
    } catch (err) {
      setToast(friendlyChatError(err));
    } finally {
      setWiping(false);
    }
  };

  return (
    <div className="card p-4 sm:p-5 mt-4">
      {/* Header: 標題 + 說明 + 操作鈕。Mobile (<sm) 改成直排（title block
          上、按鈕下）避免窄畫面把標題擠成一字一行；desktop 維持水平。 */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 mb-4">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-bg-raised border border-border">
          <Brain className="h-4 w-4 text-accent" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-text-primary">SENTRY 助手記憶</h2>
          <p className="mt-0.5 text-xs text-text-secondary leading-snug">
            AI 在和您對話時，會逐步記住您的角色脈絡、偏好設定、常見困惑等，下次回答時參考。所有記憶只屬於您（不會跨使用者）。可隨時刪除單條或全部。
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0">
          <button
            type="button"
            onClick={() => void load()}
            className="btn-secondary text-xs"
            disabled={stage.status === "loading"}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${stage.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
          </button>
          {stage.status === "ready" && stage.facts.length > 0 && (
            <button
              type="button"
              onClick={() => void onWipe()}
              className="btn-secondary text-xs text-danger"
              disabled={wiping}
            >
              <Trash2 className="h-3.5 w-3.5" /> {wiping ? "清空中…" : "忘記我"}
            </button>
          )}
        </div>
      </div>

      {toast && (
        <div className="mb-3 flex items-center gap-2 rounded border border-success/30 bg-success/10 p-2 text-xs text-success">
          <CheckCircle2 className="h-3.5 w-3.5" />
          <span className="flex-1">{toast}</span>
          <button className="text-2xs underline" onClick={() => setToast(null)}>關閉</button>
        </div>
      )}

      {stage.status === "loading" && <Skeleton className="h-20 w-full" />}
      {stage.status === "error" && (
        <div className="flex items-start gap-2 rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{stage.message}</span>
        </div>
      )}
      {stage.status === "ready" && stage.facts.length === 0 && (
        <p className="text-xs text-text-muted py-4 text-center">
          目前還沒有任何記憶。和 SENTRY 助手多聊幾次，它就會逐步認識您。
        </p>
      )}
      {stage.status === "ready" && stage.facts.length > 0 && (
        <ul className="space-y-2">
          {stage.facts.map((f) => (
            <li key={f.id} className="rounded border border-border bg-bg-raised p-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
                  <span className="text-2xs uppercase tracking-wider rounded px-1.5 py-0.5 bg-bg-base border border-border text-text-muted">
                    {KIND_LABEL_ZH[f.kind] ?? f.kind}
                  </span>
                  <span className="text-2xs text-text-muted">信心 {(f.confidence * 100).toFixed(0)}%</span>
                  <span className="text-2xs text-text-muted">·</span>
                  <span className="text-2xs text-text-muted">{formatTaipeiDateTime(f.updated_at)}</span>
                  {!f.content_zh && (
                    <span className="text-2xs text-warning whitespace-nowrap">（記錄為英文，下次延長記憶時自動補上中文）</span>
                  )}
                </div>
                {/* 顯示優先用 content_zh（migration 0031 後 AI 同步產出
                    繁中版本），舊資料無中文版時 fallback 到英文 content
                    + 上面那行黃色說明告知 user 為什麼還是英文。 */}
                <p className="text-xs text-text-secondary leading-relaxed break-words">
                  {f.content_zh ?? f.content}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void onDelete(f)}
                className="btn-secondary text-2xs text-danger flex-shrink-0 self-start"
                disabled={busyId === f.id}
                aria-label="刪除這條記憶"
              >
                <Trash2 className="h-3 w-3" /> {busyId === f.id ? "刪除中…" : "刪除"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BoundCard({ line, busy, onUnlink }: { line: LineStatus; busy: boolean; onUnlink: () => void }): React.ReactElement {
  return (
    <div className="rounded-lg border border-success/30 bg-success/5 p-4">
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle2 className="h-4 w-4 text-success" />
        <span className="text-sm font-medium text-text-primary">已綁定</span>
      </div>
      <dl className="text-xs space-y-1">
        <div className="flex gap-2">
          <dt className="text-text-muted w-20 flex-shrink-0">LINE 名稱</dt>
          <dd className="text-text-primary">{line.display_name ?? "（未取得，請先在 LINE 收一封系統通知）"}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-text-muted w-20 flex-shrink-0">LINE userId</dt>
          <dd className="font-mono text-text-secondary">{line.line_user_id_prefix ?? "—"}</dd>
        </div>
        {line.linked_at && (
          <div className="flex gap-2">
            <dt className="text-text-muted w-20 flex-shrink-0">最後更新</dt>
            <dd className="text-text-secondary">{formatTaipeiDateTime(line.linked_at)}</dd>
          </div>
        )}
      </dl>
      <div className="mt-3 flex justify-end">
        <button className="btn-secondary text-xs text-danger" onClick={onUnlink} disabled={busy}>
          {busy ? "處理中…" : "解除綁定"}
        </button>
      </div>
    </div>
  );
}

function UnboundCard(): React.ReactElement {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-bg-raised p-3">
        <p className="text-xs text-text-secondary leading-relaxed">
          請在 LINE 應用程式內開啟下列頁面完成綁定。系統會透過 LINE 認證取得您的身分，與目前登入的帳號綁定後即生效。
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {/* Mobile path — tap to open LINE app directly */}
        <div className="rounded-lg border border-border bg-bg-surface p-4 flex flex-col">
          <div className="flex items-center gap-2 mb-1">
            <Smartphone className="h-4 w-4 text-accent" />
            <span className="text-sm font-medium text-text-primary">手機已在用 dashboard</span>
          </div>
          <p className="text-2xs text-text-muted leading-relaxed mb-3 flex-1">
            點下方按鈕，會直接呼叫 LINE 開啟綁定頁面（如未安裝 LINE，會跳到瀏覽器版）。
          </p>
          <a
            href={LIFF_ME_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-sm w-full inline-flex items-center justify-center gap-1.5"
          >
            在 LINE 中開啟 <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>

        {/* Desktop path — scan QR with phone */}
        <DesktopQrCard liffUrl={LIFF_ME_URL} />
      </div>

      <details className="rounded-lg border border-border bg-bg-raised p-3 text-xs text-text-secondary">
        <summary className="cursor-pointer text-text-primary font-medium">詳細步驟</summary>
        <ol className="mt-2 list-decimal list-inside space-y-1 leading-relaxed">
          <li>在手機 LINE App 中開啟左邊任一連結（直接點按鈕，或用手機 LINE 掃 QR code）。</li>
          <li>綁定頁面會偵測您目前的後台登入狀態（已自動帶入），顯示「綁定 LINE」按鈕。</li>
          <li>點擊「綁定 LINE」確認；綁定成功後此頁狀態會自動更新（重新整理一次即可）。</li>
          <li>之後您發出的所有通知會直接送到此 LINE 帳號；可在此頁隨時解綁。</li>
        </ol>
      </details>
    </div>
  );
}

function DesktopQrCard({ liffUrl }: { liffUrl: string }): React.ReactElement {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(liffUrl, { errorCorrectionLevel: "M", margin: 1, scale: 6, color: { dark: "#0a0a0c", light: "#ffffff" } })
      .then((url) => { if (!cancelled) setDataUrl(url); })
      .catch((e) => { if (!cancelled) setErr((e as Error).message); });
    return () => { cancelled = true; };
  }, [liffUrl]);

  return (
    <div className="rounded-lg border border-border bg-bg-surface p-4 flex flex-col">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-medium text-text-primary">電腦上開 dashboard</span>
      </div>
      <p className="text-2xs text-text-muted leading-relaxed mb-3 flex-1">
        在手機上用 LINE 掃描下方 QR Code，會直接打開綁定頁。
      </p>
      <div className="flex items-center justify-center bg-white rounded p-2 min-h-[180px]">
        {err && <span className="text-2xs text-danger">QR 產生失敗：{err}</span>}
        {!err && !dataUrl && <Skeleton className="h-40 w-40" />}
        {dataUrl && <img src={dataUrl} alt="LINE 綁定頁 QR" className="h-40 w-40" />}
      </div>
    </div>
  );
}
